import { useEffect, useMemo, useState } from 'react';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider } from '@mui/material/styles';

import Router from './pages/Router';
import Snack from './components/AgSnack';
import Loading from './components/Loading';
import { ModalProvider } from './redux/hooks/modal';
import AgErrorBoundary from './components/AgErrorBoundary';
import loadMessages, {
  AllLanguageMessages,
  FALL_BACK_LOCAL,
} from './components/Language';

import store, { persistor } from './redux/store';
import { getAPIVersion } from './redux/slices/global';
import { ToastProvider } from './redux/hooks/toast';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import {
  selectApiErrorMessage,
  selectErrorMessage,
  selectRequestedPath,
  selectThemeColors,
  setApiErrorMessage,
  setErrorMessage,
  store as storeGlobals,
} from './redux/slices/global';
import { createAgTheme } from './styles/theme';
import { ROOT_PATHS } from './Constants';
import { AgColor } from './stories/Styles/AgColor';
// Import the loadMessages function

const setRequestedPath = (path: string): string | null => {
  if (
    path.indexOf(ROOT_PATHS.LOGIN) !== 0 &&
    path.indexOf(ROOT_PATHS.RESET_PW) !== 0 &&
    path.indexOf(ROOT_PATHS.E404) !== 0 &&
    path.indexOf(ROOT_PATHS.USER_SELECT) !== 0
  ) {
    return path;
  }
  return null;
};

// window.onerror = function(error) {
//   console.error('test3', error);
//   return true;
// };

const DynamicThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const themeColors = useAppSelector(selectThemeColors);
  const theme = useMemo(() => createAgTheme(themeColors), [themeColors]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

function App() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const oldPath = useAppSelector(selectRequestedPath);
  const errorMessage = useAppSelector(selectErrorMessage);
  const apiErrorMessage = useAppSelector(selectApiErrorMessage);
  const requestedPath = setRequestedPath(location.pathname);
  const locale = useAppSelector((state) => state.language.language);
  const [loading, setLoading] = useState(true);
  const localeMessage = useAppSelector((state) => state.language.localeMessage);
  // Add a new state variable to hold the loaded messages
  const [messages, setMessages] = useState<AllLanguageMessages>({});
  // Load the messages when the component mounts
  useEffect(() => {
    dispatch(getAPIVersion());
    loadMessages().then((loadedMessages) => {
      setMessages(loadedMessages);
      setLoading(false);
    });
  }, [dispatch]);

  //[Aqeel]: I have wrapped it with useEffect to fix the error in the terminal.
  useEffect(() => {
    if (requestedPath && requestedPath !== oldPath) {
      // [GA]:  we will store current authenticated path in case we become authorized
      //        this allows forwarding back after login to last know page
      dispatch(storeGlobals({ requestedPath }));
    }
  }, [dispatch, oldPath, requestedPath]);

  const clearError = async () => {
    await dispatch(setErrorMessage(null));
  };

  const clearApiError = async () => {
    await dispatch(setApiErrorMessage(null));
  };

  const handleError = (error: { message: string }) => {
    // eslint-disable-next-line no-console
    // console.log('language file is not complete, please update', error.message);
  };

  const combinedMessages = useMemo(() => {
    return {
      ...messages[FALL_BACK_LOCAL], // English messages serve as the fallback
      ...localeMessage, // Overlay with messages for the target locale, if available
    };
  }, [localeMessage, messages]);

  return (
    <PersistGate loading={null} persistor={persistor}>
      <Provider store={store}>
        <DynamicThemeProvider>
          {loading ? (
            <Loading showLogo />
          ) : (
            <IntlProvider
              locale={locale}
              defaultLocale={FALL_BACK_LOCAL}
              messages={combinedMessages}
              onError={(error) => {
                handleError(error);
              }}
            >
              {errorMessage && (
                <Snack
                  msg={errorMessage}
                  onClose={clearError}
                  severity='error'
                />
              )}
              {apiErrorMessage && (
                <Snack
                  msg={apiErrorMessage}
                  onClose={clearApiError}
                  severity='error'
                />
              )}
              <main
                style={{
                  height: '100vh',
                  backgroundColor: AgColor.background,
                }}
              >
                <AgErrorBoundary>
                  <ToastProvider>
                    <ModalProvider>
                      <Router />
                    </ModalProvider>
                  </ToastProvider>
                </AgErrorBoundary>
              </main>
            </IntlProvider>
          )}
        </DynamicThemeProvider>
      </Provider>
    </PersistGate>
  );
}

export default App;
